import { STRINGS } from '@/constants/strings';
import { useFilterPlayer } from '@/features/player/hooks/use-filter-player';
import { createStyles } from '@/hooks';
import { colors } from '@/theme/colors';
import { useEffect } from 'react';
import type { SharedValue } from 'react-native-reanimated';

import type { PaginatedResponse } from '@/libs/api';

export type ListPlayersHeaderProps = {
  scrollY: SharedValue<number>;
  buttonCreateProfileHeight: SharedValue<number>;
  /** Phone: reported height of `children` (headerActionButton) for list top spacer. */
  onHeaderActionLayout?: (height: number) => void;
  onChangeSearchText?: (text: string) => void;
  onClearSearchText?: () => void;
  searchText?: string;
  paginatedMeta?: PaginatedResponse<player.PlayerLookupData>['meta'];
  onChangePage?: (page: number) => void;

  titleResultsSummary?: string;
  isResultsSummaryVisible?: boolean;
  extraComponent?: React.ReactNode;
};

/** Same threshold as `ListPlayer` grid hide offset (scroll Y). */
export const SCROLL_COLLAPSE_THRESHOLD = 30;

export function useListPlayersHeaderCommon({
  paginatedMeta,
  isResultsSummaryVisible = true,
}: {
  paginatedMeta?: PaginatedResponse<player.PlayerLookupData>['meta'];
  isResultsSummaryVisible?: boolean;
}) {
  const totalPages = paginatedMeta?.totalPages;
  const currentPage = paginatedMeta?.page;
  const totalResult = paginatedMeta?.total ?? 0;
  const limit = paginatedMeta?.limit ?? 0;

  const startIndex =
    totalResult > 0 && limit > 0 ? (Math.max(0, currentPage ?? 1) - 1) * limit + 1 : 0;
  const endIndex =
    totalResult > 0 && limit > 0 ? Math.min((currentPage ?? 1) * limit, totalResult) : 0;

  // TODO: need refactor, move logic to component sort and filter player
  const {
    open,
    setOpen,
    level,
    lastActive,
    draftLevel,
    setDraftLevel,
    draftLastActive,
    setDraftLastActive,
    filterButtonLabel,
    closeWithoutSave,
    saveAndClose,
    LEVELS,
    lastActiveOptions,
    sortOpen,
    setSortOpen,
    sortKey,
    sortOrder,
    sortDraft,
    setSortDraft,
    sortButtonLabel,
    closeSortWithoutSave,
    saveSortAndClose,
    forceSortToNameAsc,
  } = useFilterPlayer();

  const selectedLastActiveLabel = lastActiveOptions.find(o => o.key === lastActive)?.label ?? '';

  const resultSummary =
    isResultsSummaryVisible && totalResult > 0
      ? lastActive === 'show_all'
        ? STRINGS.player.listPlayers.resultsSummaryShowAll(startIndex, endIndex, totalResult)
        : STRINGS.player.listPlayers.resultsSummaryWithLastActive(
            startIndex,
            endIndex,
            totalResult,
            selectedLastActiveLabel,
          )
      : '';

  useEffect(() => {
    if (!open) return;
    // Open sheet = start editing from saved filters.
    setDraftLevel(level);
    setDraftLastActive(lastActive);
  }, [lastActive, level, open, setDraftLastActive, setDraftLevel]);

  useEffect(() => {
    if (level == null) return;
    if (sortKey === 'level') {
      forceSortToNameAsc();
      return;
    }
    if (sortDraft.key === 'level') {
      setSortDraft({ key: 'name', order: 'asc' });
    }
  }, [forceSortToNameAsc, level, setSortDraft, sortDraft.key, sortKey]);

  return {
    totalPages,
    currentPage,
    resultSummary,
    open,
    setOpen,
    level,
    lastActive,
    draftLevel,
    setDraftLevel,
    draftLastActive,
    setDraftLastActive,
    filterButtonLabel,
    closeWithoutSave,
    saveAndClose,
    LEVELS,
    lastActiveOptions,
    sortOpen,
    setSortOpen,
    sortKey,
    sortOrder,
    sortDraft,
    setSortDraft,
    sortButtonLabel,
    closeSortWithoutSave,
    saveSortAndClose,
  };
}

export const useStyles = createStyles(({ scale }) => ({
  createProfileButton: {
    width: '100%',
    height: scale(44),
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  createProfileButtonText: {
    textAlign: 'center',
  },
  actionButton: {
    flex: 1,
    height: scale(32),
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  actionButtonSort: {},
  tabletActionsContainer: {
    flexShrink: 0,
  },
  tabletActionButton: {
    width: scale(140),
    height: scale(40),
    flex: 0,
  },
  tabletSearchContainer: {
    // width: scale(360),
    // flex: 1,
    flexShrink: 0,
  },
  tabletCreateButton: {
    width: scale(200),
    height: scale(40),
    flexShrink: 0,
  },
  resultsSummary: {
    color: colors['6'],
    textAlign: 'center',
  },
  tabletResultsSummary: {
    textAlign: 'left',
  },
  summaryText: { minWidth: scale(200), textAlign: 'right' },
  createProfileButtonContainer: {
    zIndex: 2,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
}));
